import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { QueryClient, QueryClientProvider, useMutation } from '@tanstack/react-query';

// Shared Kernel Imports
import { Navbar, Footer, AnimatedToast, ConfirmDeleteModal, OfflineIndicator, ErrorBoundary } from './shared/components/index.js';
import { useModalStore } from './shared/store/useModalStore.js';
import { api } from './shared/api/api.js';
import { getEffectiveApiBaseUrl } from './shared/api/client.js';
import { syncAllPendingCovers } from './shared/utils/dexieDb.js';
import type { Book } from './shared/types/index.js';
import { slugify } from './shared/utils/stringUtils.js';

// Feature-Based Domain Imports
import { AuthModals, UserProfile, useAuthStore } from './features/auth/index.js';
import { BookList, BookDetailModal, BookFormModal } from './features/books/index.js';
import { EditionTreeModal, CreateEditionModal } from './features/editions/index.js';
import { CertificateModal } from './features/uibn/index.js';
import { BookVerificationModal } from './features/verification/index.js';
import { EmbedBadgeModal } from './features/badges/index.js';
import { ScannerModal, ScanHistoryView } from './features/scanner/index.js';
import { DashboardView } from './features/dashboard/index.js';
import { LandingPage } from './features/landing/index.js';
import { NotificationDrawer, useNotificationStore } from './features/notifications/index.js';
import { ApiDocsView } from './features/admin/index.js';
import { BlogListView, BlogPostView, BlogManager } from './features/blog/index.js';
import { PublicShowcaseView, ShowcaseDirectoryView } from './features/showcase/index.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30, // 30 seconds fresh cache
      gcTime: 1000 * 60 * 10, // 10 minutes cache retention
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function AppContent() {
  const [currentTab, setCurrentTabState] = useState<'landing' | 'books' | 'dashboard' | 'history' | 'api-docs' | 'profile' | 'blog' | 'admin-blog' | 'showcase-directory' | 'showcase'>('landing');
  const [prevTab, setPrevTab] = useState<typeof currentTab>('landing');
  const [showcaseParams, setShowcaseParams] = useState<{ type: 'penulis' | 'penerbit' | 'organisasi'; identifier: string } | null>(null);

  const setCurrentTab = (tab: typeof currentTab | ((prev: typeof currentTab) => typeof currentTab)) => {
    setCurrentTabState((prev) => {
      const next = typeof tab === 'function' ? tab(prev) : tab;
      setPrevTab(prev);
      return next;
    });
  };

  const tabIndexes: Record<string, number> = {
    landing: 0,
    books: 1,
    dashboard: 2,
    history: 3,
    profile: 4,
    blog: 5,
    'api-docs': 6,
    'admin-blog': 7,
  };

  const currentIndex = tabIndexes[currentTab] ?? 0;
  const prevIndex = tabIndexes[prevTab] ?? 0;
  const slidingRight = currentIndex > prevIndex;

  const tabVariants = {
    initial: (isRight: boolean) => ({
      opacity: 0,
      x: isRight ? 24 : -24,
      filter: 'blur(3px)',
    }),
    animate: {
      opacity: 1,
      x: 0,
      filter: 'blur(0px)',
    },
    exit: (isRight: boolean) => ({
      opacity: 0,
      x: isRight ? -24 : 24,
      filter: 'blur(3px)',
    }),
  };

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [verificationUibn, setVerificationUibn] = useState<string | null>(null);
  const [blogSlug, setBlogSlug] = useState<string | null>(null);
  const [activeEmbedBook, setActiveEmbedBook] = useState<Book | null>(null);

  const {
    viewingBook,
    setViewingBook,
    certBook,
    setCertBook,
    treeBook,
    setTreeBook,
    editingBook,
    setEditingBook,
    isFormOpen,
    setIsFormOpen,
    createEditionParent,
    setCreateEditionParent,
    isScannerOpen,
    setIsScannerOpen,
    deletingBook,
    setDeletingBook,
  } = useModalStore();

  const {
    user,
    loadInitialAuth,
    isLoginModalOpen,
    isRegisterModalOpen,
    openLoginModal,
    openRegisterModal,
    closeLoginModal,
    closeRegisterModal
  } = useAuthStore();
  const { notifications, showToast } = useNotificationStore();
  const prevUserRef = useRef(user);

  // Automatically redirect to 'books' (Katalog & Manajemen Buku) upon login / registration
  useEffect(() => {
    if (!prevUserRef.current && user) {
      setCurrentTab('books');
    }
    prevUserRef.current = user;
  }, [user]);

  useEffect(() => {
    loadInitialAuth();
    // Background sync Dexie covers with R2 if connected
    syncAllPendingCovers().catch(console.error);

    // Check if user came from an embedded badge click or public showcase share link
    const checkVerifyParam = () => {
      const searchParams = new URLSearchParams(window.location.search);
      const verifyQuery = searchParams.get('verify') || searchParams.get('uibn');
      const penerbitQuery = searchParams.get('penerbit');
      const penulisQuery = searchParams.get('penulis');

      if (verifyQuery) {
        setVerificationUibn(verifyQuery.trim());
      } else if (penerbitQuery) {
        setShowcaseParams({ type: 'penerbit', identifier: decodeURIComponent(penerbitQuery.trim()) });
        setCurrentTab('showcase');
      } else if (penulisQuery) {
        setShowcaseParams({ type: 'penulis', identifier: decodeURIComponent(penulisQuery.trim()) });
        setCurrentTab('showcase');
      } else {
        // Check pathname & hash
        const pathname = window.location.pathname;
        const hash = window.location.hash;

        if (hash.startsWith('#verify=')) {
          const code = hash.replace('#verify=', '');
          if (code) setVerificationUibn(decodeURIComponent(code).trim());
        } else if (pathname.startsWith('/verify/')) {
          const pathCode = pathname.replace('/verify/', '');
          if (pathCode) setVerificationUibn(decodeURIComponent(pathCode).trim());
        } else if (pathname.startsWith('/buku/')) {
          const rawSlug = pathname.replace(/^\/buku\//, '');
          const parts = rawSlug.split('/');
          const pathCode = parts[parts.length - 1] || parts[0];
          if (pathCode) setVerificationUibn(decodeURIComponent(pathCode).trim());
        } else if (pathname.startsWith('/penerbit/')) {
          const id = pathname.replace('/penerbit/', '');
          if (id) {
            setShowcaseParams({ type: 'penerbit', identifier: decodeURIComponent(id).trim() });
            setCurrentTab('showcase');
          }
        } else if (pathname.startsWith('/organisasi/')) {
          const id = pathname.replace('/organisasi/', '');
          if (id) {
            setShowcaseParams({ type: 'organisasi', identifier: decodeURIComponent(id).trim() });
            setCurrentTab('showcase');
          }
        } else if (pathname.startsWith('/komunitas/')) {
          const id = pathname.replace('/komunitas/', '');
          if (id) {
            setShowcaseParams({ type: 'organisasi', identifier: decodeURIComponent(id).trim() });
            setCurrentTab('showcase');
          }
        } else if (pathname.startsWith('/penulis/')) {
          const id = pathname.replace('/penulis/', '');
          if (id) {
            setShowcaseParams({ type: 'penulis', identifier: decodeURIComponent(id).trim() });
            setCurrentTab('showcase');
          }
        } else if (pathname.startsWith('/showcase/penerbit/')) {
          const id = pathname.replace('/showcase/penerbit/', '');
          if (id) {
            setShowcaseParams({ type: 'penerbit', identifier: decodeURIComponent(id).trim() });
            setCurrentTab('showcase');
          }
        } else if (pathname.startsWith('/showcase/organisasi/')) {
          const id = pathname.replace('/showcase/organisasi/', '');
          if (id) {
            setShowcaseParams({ type: 'organisasi', identifier: decodeURIComponent(id).trim() });
            setCurrentTab('showcase');
          }
        } else if (pathname.startsWith('/showcase/komunitas/')) {
          const id = pathname.replace('/showcase/komunitas/', '');
          if (id) {
            setShowcaseParams({ type: 'organisasi', identifier: decodeURIComponent(id).trim() });
            setCurrentTab('showcase');
          }
        } else if (pathname.startsWith('/showcase/penulis/')) {
          const id = pathname.replace('/showcase/penulis/', '');
          if (id) {
            setShowcaseParams({ type: 'penulis', identifier: decodeURIComponent(id).trim() });
            setCurrentTab('showcase');
          }
        } else if (pathname === '/direktori' || pathname === '/showcase') {
          setCurrentTab('showcase-directory');
        } else if (pathname.startsWith('/blog/')) {
          const slug = pathname.replace('/blog/', '');
          if (slug) {
            setBlogSlug(decodeURIComponent(slug).trim());
            setCurrentTab('blog');
          }
        } else if (pathname === '/blog') {
          setBlogSlug(null);
          setCurrentTab('blog');
        } else if (pathname.startsWith('/api/docs/swagger') || pathname.startsWith('/docs/swagger') || pathname.startsWith('/swagger')) {
          const apiBase = getEffectiveApiBaseUrl();
          if (apiBase) {
            window.location.replace(`${apiBase}/api/docs/swagger`);
            return;
          }
        } else if (pathname.startsWith('/api/docs/openapi.json') || pathname.startsWith('/docs/openapi.json') || pathname === '/openapi.json') {
          const apiBase = getEffectiveApiBaseUrl();
          if (apiBase) {
            window.location.replace(`${apiBase}/api/docs/openapi.json`);
            return;
          }
        } else if (pathname === '/api-docs' || pathname === '/docs' || pathname === '/admin/docs') {
          setCurrentTab('api-docs');
        }
      }
    };

    checkVerifyParam();
    window.addEventListener('popstate', checkVerifyParam);

    // Global session expired handler (triggers on 401 Unauthorized)
    const handleSessionExpired = (event: Event) => {
      const customEvent = event as CustomEvent<{ message?: string }>;
      const message = customEvent.detail?.message || 'Sesi Anda telah kedaluwarsa. Silakan masuk kembali.';
      useAuthStore.getState().logout();
      useNotificationStore.getState().showToast('Sesi Berakhir', message, 'error');
      openLoginModal();
    };
    window.addEventListener('uibn:session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('popstate', checkVerifyParam);
      window.removeEventListener('uibn:session-expired', handleSessionExpired);
    };
  }, [loadInitialAuth, openLoginModal]);

  // If public visitor (!user) tries to access restricted tabs, force redirect to landing
  useEffect(() => {
    if (!user && !['landing', 'blog', 'showcase-directory', 'showcase'].includes(currentTab)) {
      setCurrentTab('landing');
    }
  }, [user, currentTab]);

  // If user is not admin and tries to access api-docs, redirect to landing
  useEffect(() => {
    if (currentTab === 'api-docs' && user?.role !== 'admin') {
      setCurrentTab('landing');
    }
  }, [user, currentTab]);

  // Global delete mutation for draft books
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteBook(id),
    onSuccess: (_data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: ['books'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      queryClient.invalidateQueries({ queryKey: ['showcase'] });
      if (viewingBook?.id === deletedId) {
        setViewingBook(null);
      }
      if (editingBook?.id === deletedId) {
        setEditingBook(null);
        setIsFormOpen(false);
      }
      setDeletingBook(null);
      showToast('Draft Dihapus', 'Data draft UIBN berhasil dihapus dari katalog', 'info');
    },
    onError: (err: any) => {
      setDeletingBook(null);
      showToast('Gagal Menghapus', err.message || 'Draft UIBN tidak dapat dihapus', 'error');
    },
  });

  const handleDeleteBookGlobal = (id: string, title: string) => {
    setDeletingBook({ id, title });
  };

  const handleOpenShowcase = (type: 'penulis' | 'penerbit', identifier: string) => {
    setShowcaseParams({ type, identifier });
    setCurrentTab('showcase');
    window.history.pushState({}, '', `/${type}/${encodeURIComponent(identifier)}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900 font-sans selection:bg-indigo-500 selection:text-white">
        <OfflineIndicator />
        {/* Global Navigation Bar */}
        <Navbar
          currentTab={currentTab}
          onSelectTab={setCurrentTab}
          onOpenLogin={openLoginModal}
          onOpenRegister={openRegisterModal}
          onOpenProfile={() => setIsProfileOpen(true)}
          onOpenShowcase={handleOpenShowcase}
        />

        {/* Main Content Area with Page & Tab Transition Animations */}
        <main className="flex-1 max-w-7xl 2xl:max-w-[1600px] 3xl:max-w-[1760px] w-full mx-auto px-4 sm:px-6 lg:px-8 2xl:px-12 pt-6 sm:pt-8 overflow-x-clip">
          <AnimatePresence mode="wait" custom={slidingRight}>
            <motion.div
              key={user ? `${user.id}-${currentTab}` : `guest-${currentTab}`}
              custom={slidingRight}
              variants={tabVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
              className="w-full h-full"
            >
              {currentTab === 'landing' && (
                <LandingPage
                  onNavigateToDashboard={() => {
                    if (!user) {
                      openLoginModal();
                    } else {
                      setCurrentTab('books');
                    }
                  }}
                  onOpenLogin={openLoginModal}
                  onOpenRegister={openRegisterModal}
                  onSelectTab={(tab) => {
                    if (!user && !['landing', 'blog', 'showcase-directory', 'showcase'].includes(tab)) {
                      openLoginModal();
                    } else {
                      setCurrentTab(tab);
                    }
                  }}
                />
              )}

              {user && currentTab === 'books' && <BookList />}

              {user && currentTab === 'dashboard' && <DashboardView />}

              {user && currentTab === 'history' && <ScanHistoryView />}

              {user && user.role === 'admin' && currentTab === 'api-docs' && <ApiDocsView />}

              {user && currentTab === 'profile' && (
                <div className="py-4">
                  <UserProfile isModal={false} />
                </div>
              )}

              {currentTab === 'blog' && (
                <AnimatePresence mode="wait">
                  {blogSlug ? (
                    <motion.div
                      key={`blog-post-${blogSlug}`}
                      initial={{ opacity: 0, y: 15, filter: 'blur(3px)' }}
                      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                      exit={{ opacity: 0, y: -15, filter: 'blur(3px)' }}
                      transition={{ duration: 0.25, ease: 'easeOut' }}
                    >
                      <BlogPostView 
                        slug={blogSlug} 
                        onBack={() => {
                          setBlogSlug(null);
                          window.history.pushState({}, '', '/blog');
                        }}
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="blog-list"
                      initial={{ opacity: 0, y: 15, filter: 'blur(3px)' }}
                      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                      exit={{ opacity: 0, y: -15, filter: 'blur(3px)' }}
                      transition={{ duration: 0.25, ease: 'easeOut' }}
                    >
                      <BlogListView 
                        onSelectPost={(slug) => {
                          setBlogSlug(slug);
                          window.history.pushState({}, '', `/blog/${slug}`);
                        }}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              )}

              {user && user.role === 'admin' && currentTab === 'admin-blog' && <BlogManager />}

              {currentTab === 'showcase-directory' && (
                <ShowcaseDirectoryView
                  onSelectShowcase={(type, identifier) => {
                    setShowcaseParams({ type, identifier });
                    setCurrentTab('showcase');
                    window.history.pushState({}, '', `/${type}/${slugify(identifier)}`);
                  }}
                />
              )}

              {currentTab === 'showcase' && (() => {
                const activeShowcase = showcaseParams || (user ? {
                  type: user.role === 'organisasi' ? ('organisasi' as const) : (user.role === 'penerbit' ? ('penerbit' as const) : ('penulis' as const)),
                  identifier: user.organization || user.name || user.id,
                } : null);

                if (!activeShowcase) return null;

                return (
                  <PublicShowcaseView
                    type={activeShowcase.type}
                    identifier={activeShowcase.identifier}
                    onBackToDirectory={() => {
                      setCurrentTab('showcase-directory');
                      window.history.pushState({}, '', '/direktori');
                    }}
                  />
                );
              })()}
            </motion.div>
          </AnimatePresence>
        </main>

        {/* Global Toast Notification */}
        <AnimatedToast />

        {/* Global Footer */}
        <Footer />

        {/* User Profile Modal */}
        {user && (
          <UserProfile
            isOpen={isProfileOpen}
            isModal={true}
            onClose={() => setIsProfileOpen(false)}
          />
        )}

        {/* Auth Modals */}
        <AuthModals
          isLoginOpen={isLoginModalOpen}
          isRegisterOpen={isRegisterModalOpen}
          onCloseLogin={closeLoginModal}
          onCloseRegister={closeRegisterModal}
          onOpenLogin={openLoginModal}
          onOpenRegister={openRegisterModal}
          onLoginSuccess={() => setCurrentTab('books')}
        />

        {/* Notification Drawer */}
        <NotificationDrawer />

        {/* Global Verification Modal (Triggered by embed badge clicks or URL query) */}
        <BookVerificationModal
          uibnOrId={verificationUibn}
          isOpen={!!verificationUibn}
          onClose={() => {
            setVerificationUibn(null);
            // Clean URL without full reload
            if (window.location.pathname.startsWith('/buku/')) {
              window.history.pushState({}, document.title, '/');
            } else if (window.location.search.includes('verify=') || window.location.search.includes('uibn=') || window.location.hash.startsWith('#verify=')) {
              window.history.replaceState({}, document.title, window.location.pathname);
            }
          }}
          onOpenFullDetail={(book) => {
            setVerificationUibn(null);
            setViewingBook(book);
            window.history.pushState({}, '', `/buku/${slugify(book.title)}`);
          }}
          onOpenEditionTree={(book) => {
            setTreeBook(book);
          }}
          onOpenEmbedModal={(book) => {
            setActiveEmbedBook(book);
          }}
        />

        {/* Global Book Detail & Barcode Modal */}
        <AnimatePresence>
          {viewingBook && (
            <BookDetailModal
              book={viewingBook}
              onClose={() => {
                setViewingBook(null);
                if (window.location.pathname.startsWith('/buku/')) {
                  window.history.pushState({}, document.title, '/');
                }
              }}
              onBookUpdated={(updated) => {
                setViewingBook(updated);
                queryClient.invalidateQueries({ queryKey: ['books'] });
                queryClient.invalidateQueries({ queryKey: ['stats'] });
                queryClient.invalidateQueries({ queryKey: ['showcase'] });
              }}
              onEditRequested={(b) => {
                setEditingBook(b);
                setIsFormOpen(true);
              }}
              onDeleteRequested={handleDeleteBookGlobal}
            />
          )}
        </AnimatePresence>

        {/* Global Book Form Modal */}
        <BookFormModal
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditingBook(null);
          }}
          initialBook={editingBook}
          onSuccess={(book) => {
            queryClient.invalidateQueries({ queryKey: ['books'] });
            queryClient.invalidateQueries({ queryKey: ['stats'] });
            queryClient.invalidateQueries({ queryKey: ['showcase'] });
            if (editingBook) {
              showToast(
                'Perubahan Disimpan',
                `Metadata buku "${book?.title || editingBook.title}" berhasil diperbarui`,
                'success'
              );
            } else {
              showToast(
                'Pendaftaran Berhasil',
                `Buku "${book?.title || 'baru'}" berhasil didaftarkan di UIBN`,
                'success'
              );
            }
          }}
          onDeleteRequested={handleDeleteBookGlobal}
          onOpenProfile={() => setIsProfileOpen(true)}
        />

        {/* Global E-Certificate Modal */}
        <CertificateModal
          book={certBook}
          isOpen={!!certBook}
          onClose={() => setCertBook(null)}
        />

        {/* Global Edition Tree Modal */}
        <EditionTreeModal
          book={treeBook}
          isOpen={!!treeBook}
          onClose={() => setTreeBook(null)}
          onSelectBook={(selected) => {
            setTreeBook(null);
            setViewingBook(selected);
          }}
          onBookCreated={() => {
            queryClient.invalidateQueries({ queryKey: ['books'] });
            queryClient.invalidateQueries({ queryKey: ['stats'] });
            queryClient.invalidateQueries({ queryKey: ['showcase'] });
          }}
        />

        {/* Global Create New Edition Modal */}
        <CreateEditionModal
          parentBook={createEditionParent}
          isOpen={!!createEditionParent}
          onClose={() => setCreateEditionParent(null)}
          onEditionCreated={(newBook) => {
            queryClient.invalidateQueries({ queryKey: ['books'] });
            queryClient.invalidateQueries({ queryKey: ['stats'] });
            queryClient.invalidateQueries({ queryKey: ['showcase'] });
            setTreeBook(newBook);
          }}
        />

        {/* Global Scanner Modal */}
        <AnimatePresence>
          {isScannerOpen && (
            <ScannerModal
              isOpen={isScannerOpen}
              onClose={() => setIsScannerOpen(false)}
              onBookFound={(book) => {
                setIsScannerOpen(false);
                setViewingBook(book);
                window.history.pushState({}, '', `/buku/${slugify(book.title)}`);
              }}
            />
          )}
        </AnimatePresence>

        {/* Global Embed Badge Modal */}
        {activeEmbedBook && (
          <EmbedBadgeModal
            book={activeEmbedBook}
            isOpen={!!activeEmbedBook}
            onClose={() => setActiveEmbedBook(null)}
            onOpenVerifyModal={(uibn) => {
              setActiveEmbedBook(null);
              setVerificationUibn(uibn);
            }}
          />
        )}

        {/* Global Confirm Delete Modal */}
        <ConfirmDeleteModal
          isOpen={Boolean(deletingBook)}
          title="Hapus Draft UIBN"
          description={
            deletingBook ? (
              <>
                Apakah Anda yakin ingin menghapus draft naskah{' '}
                <span className="font-semibold text-slate-900">"{deletingBook.title}"</span>?
                Tindakan ini tidak dapat dibatalkan.
              </>
            ) : undefined
          }
          isPending={deleteMutation.isPending}
          onClose={() => setDeletingBook(null)}
          onConfirm={() => {
            if (deletingBook) {
              deleteMutation.mutate(deletingBook.id);
            }
          }}
        />
      </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AppContent />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
